import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, CheckCircle, XCircle, Eye, EyeOff, Sparkles } from "lucide-react";
import SenArtisteLogo from "@/components/SenArtisteLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setUser(data.user);
        toast.success("Compte activé avec succès !");
        
        // Store token and user
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        
        // Redirect based on role after 3 seconds
        setTimeout(() => {
          if (data.user?.role === "APPORTEUR") {
            navigate("/apporteur/dashboard");
          } else if (data.user?.role === "SUPER_ADMIN") {
            navigate("/admin");
          } else {
            navigate("/");
          }
        }, 3000);
      } else {
        if (data.detail?.includes("expiré")) {
          toast.error("Le lien a expiré. Contactez l'administrateur pour un nouveau lien.");
          setStatus("error");
        } else if (data.detail?.includes("invalide")) {
          toast.error("Lien invalide ou déjà utilisé");
          setStatus("error");
        } else {
          toast.error(data.detail || "Une erreur est survenue");
        }
      }
    } catch (error) {
      console.error("Set password error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-black via-zinc-900 to-black">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-zinc-900/50 border-zinc-800 backdrop-blur-sm">
          <CardHeader className="text-center space-y-2">
            <Link to="/" className="flex items-center justify-center gap-3 mb-4">
              <SenArtisteLogo className="w-16 h-16" />
              <span className="font-syne font-bold text-2xl text-white">Sen Artiste</span>
            </Link>
            
            {status === "form" && (
              <>
                <div className="flex justify-center mb-2">
                  <div className="bg-[#D4AF37]/20 p-3 rounded-full">
                    <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                <CardTitle className="font-syne text-2xl text-white">Bienvenue !</CardTitle>
                <CardDescription className="text-zinc-400">
                  Créez votre mot de passe pour activer votre compte apporteur
                </CardDescription>
              </>
            )}
            
            {status === "success" && (
              <>
                <CardTitle className="font-syne text-2xl text-white">Compte activé !</CardTitle>
                <CardDescription className="text-zinc-400">
                  Bienvenue dans l'équipe Sen Artiste
                </CardDescription>
              </>
            )}
            
            {status === "error" && (
              <>
                <CardTitle className="font-syne text-2xl text-white">Lien invalide</CardTitle>
                <CardDescription className="text-zinc-400">
                  Ce lien d'invitation a expiré ou est invalide
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent>
            {status === "success" && (
              <div className="text-center py-6" data-testid="set-password-success">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Bienvenue, {user?.firstName} !
                </h3>
                <p className="text-zinc-400 text-sm mb-6">
                  Votre compte apporteur est maintenant actif. Vous allez être redirigé vers votre tableau de bord.
                </p>
                <p className="text-xs text-zinc-500 mb-4">
                  Redirection automatique dans 3 secondes...
                </p>
                <Button 
                  onClick={() => navigate("/apporteur/dashboard")}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-black"
                >
                  Accéder à mon dashboard
                </Button>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-6" data-testid="set-password-error">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Oops !</h3>
                <p className="text-zinc-400 text-sm mb-6">
                  Ce lien d'invitation n'est plus valide. Contactez l'administrateur pour recevoir un nouveau lien.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full border-zinc-700 text-white hover:bg-zinc-800">
                    Retour à la connexion
                  </Button>
                </Link>
              </div>
            )}

            {status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white"
                      required
                      minLength={6}
                      data-testid="set-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500">Minimum 6 caractères</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-zinc-300">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                      required
                      data-testid="set-confirm-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-black font-semibold"
                  disabled={loading}
                  data-testid="set-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Activation...
                    </>
                  ) : (
                    "Activer mon compte"
                  )}
                </Button>

                <p className="text-xs text-center text-zinc-500 pt-2">
                  En activant votre compte, vous acceptez les{" "}
                  <Link to="/cgu" className="text-[#D4AF37] hover:underline">
                    conditions d'utilisation
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
